/**
 * Exchange rates (FR-CUR-02, FR-CUR-03, FR-CUR-04).
 *
 * Two free, no-key sources with one as fallback, cached for the day.
 *
 * The rate is fetched only to be **frozen onto the transaction at entry** — nothing here
 * is ever consulted to value a historical record. That is the whole point of FR-CUR-03:
 * re-reading a rate later would silently reprice past transactions every time the market
 * moved.
 */

import { rateToPpm, type Ppm } from '../core/money';
import { todayKey, type DateKey } from '../core/dates';

const CACHE_KEY = 'usd-inr-rate';

export interface RateResult {
  ratePpm: Ppm;
  /** The day the rate was actually fetched, which may not be today. */
  fetchedFor: DateKey;
  /** True when the live sources failed and a cached rate was used (FR-CUR-04). */
  stale: boolean;
}

/**
 * A last-resort rate, used only when no source responds and nothing is cached.
 *
 * Deliberately marked stale so the transaction records that its rate is a guess, and the
 * amount stays editable (FR-CUR-05). Refusing the entry instead would be worse: it would
 * block a USD expense because a rate API was down.
 */
const FALLBACK_PPM = rateToPpm(83);

interface CachedRate {
  ratePpm: Ppm;
  fetchedFor: DateKey;
}

function readCache(): CachedRate | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedRate;
    return Number.isFinite(parsed.ratePpm) && parsed.ratePpm > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(value: CachedRate): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // A full or disabled localStorage must not stop an entry being saved.
  }
}

async function fetchFrankfurter(signal: AbortSignal): Promise<number | null> {
  const response = await fetch('https://api.frankfurter.app/latest?from=USD&to=INR', { signal });
  if (!response.ok) return null;
  const body = (await response.json()) as { rates?: { INR?: number } };
  return body.rates?.INR ?? null;
}

async function fetchOpenErApi(signal: AbortSignal): Promise<number | null> {
  const response = await fetch('https://open.er-api.com/v6/latest/USD', { signal });
  if (!response.ok) return null;
  const body = (await response.json()) as { rates?: { INR?: number } };
  return body.rates?.INR ?? null;
}

/**
 * The USD→INR rate to freeze onto a transaction.
 *
 * Cached per day, so a session of entries makes at most one network call. Never throws:
 * the caller is in the middle of saving an expense, and a rate lookup failing is not a
 * reason to lose what they typed.
 */
export async function getUsdRate(
  { timeoutMs = 4000, now = todayKey() }: { timeoutMs?: number; now?: DateKey } = {},
): Promise<RateResult> {
  const cached = readCache();
  if (cached && cached.fetchedFor === now) {
    return { ratePpm: cached.ratePpm, fetchedFor: cached.fetchedFor, stale: false };
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    for (const source of [fetchFrankfurter, fetchOpenErApi]) {
      try {
        const rate = await source(controller.signal);
        if (rate && rate > 0) {
          const value = { ratePpm: rateToPpm(rate), fetchedFor: now };
          writeCache(value);
          return { ...value, stale: false };
        }
      } catch {
        // Try the next source.
      }
    }
  } finally {
    clearTimeout(timer);
  }

  // FR-CUR-04: carry on with the last cached rate, and record that it is stale.
  if (cached) return { ratePpm: cached.ratePpm, fetchedFor: cached.fetchedFor, stale: true };
  return { ratePpm: FALLBACK_PPM, fetchedFor: now, stale: true };
}

/** Test seam. */
export function __clearRateCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}
